
import React, { useState, useEffect, useContext } from 'react';
import ExtractSnippet from '../results/ExtractSnippet.js';
import PropTypes from 'prop-types';
import Lottie from 'lottie-react';
import {Document, Page, pdfjs} from 'react-pdf';
import ExitIcon from '../../assets/icons/exit_icon1.svg';
import Tooltip from '../common/Tooltip.js';
import CopyIcon from '../../assets/icons/copy_icon.svg';
import CheckIcon from '../../assets/icons/check_icon.svg';
import CyclingIcon1 from '../../assets/animations/cyclist_json1.json';
import CyclingIcon2 from '../../assets/animations/cyclist_json2.json';
import CyclingIcon3 from '../../assets/animations/cyclist_json3.json';
import CyclingIcon4 from '../../assets/animations/cyclist_json4.json';
import CyclingIcon5 from '../../assets/animations/cyclist_json5.json';
import CyclingIcon6 from '../../assets/animations/cyclist_json2.json';
import CyclingIcon7 from '../../assets/animations/cyclist_json5.json';
import CyclingIcon8 from '../../assets/animations/cyclist_json3.json';
import FinishFlag from '../../assets/icons/finish_flag4.png';
import FinishLine from '../../assets/icons/finish_line1.png';
import EyePDFIconOff from '../../assets/icons/eye_icon2.svg';
import EyePDFIconOn from '../../assets/icons/eye_icon3.svg';
import listOverviewIconOn from '../../assets/icons/list_icon2.svg';
import listOverviewIconOff from '../../assets/icons/list_icon.svg';
import ResultCard from '../results/ResultCard.js';
import GeneralTooltip from '../common/GeneralTooltip.js';
import SvgIcon from '../common/SvgIcon.js';
import FilePreviewModal from '../preview/FilePreviewModal.js';
import expand_icon from '../../assets/icons/expand_icon.svg';
import collapse_icon from '../../assets/icons/collapse_icon.svg';
import { AppContext } from '../../context/appContext.js';
import CloseIcon from '../../assets/icons/close_icon2.svg';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.mjs`;

function ResultsDisplay({ 
  rankedResumes, 
  jobDescriptionKeywords, 
  onRemoveKeyword, 
  editedFilenameInputs,
  previewCache,
  setPreviewCache,
  previewCacheStatus,
  setPreviewCacheStatus,
  activePreviewIndex,
  setActivePreviewIndex,
  clearPreviewCache}) {
  const [selectedResume, setSelectedResume] = useState(null);
  const [copiedSection, setCopiedSection] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  const cyclingIcons = [CyclingIcon1, CyclingIcon2, CyclingIcon3, CyclingIcon4, CyclingIcon5, CyclingIcon6, CyclingIcon7, CyclingIcon8];

  const [currentCyclingIcon, setCurrentCyclingIcon] = useState(null);
  const [copyStatus, setCopyStatus] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const { isExpanded, setIsExpanded } = useContext(AppContext);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const { isPopUp, setIsPopUp } = useContext(AppContext);

  const toggleExpand = () => setIsExpanded(prev => !prev);
  const togglePopUp = () => setIsPopUp(prev => !prev);

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text)
    .then(() => {
      setCopiedSection(section);
      setCopyStatus({ copied: true, time: Date.now() });
      setTimeout(() => setCopiedSection(null), 2000);
      setTimeout(() => setCopyStatus({}), 2000);
    })
    .catch(() => {});
  };

  useEffect(() => {}, [previewCacheStatus]);

  const handleResetPreview = () => {
    setPageNumber(1);
    setNumPages(null);
  };

  const handleCardClick = (resume) => {
    setActivePreviewIndex(resume.originalIndex);
    const detectedType = resume.type || 
      (resume.filename?.toLowerCase().endsWith('.pdf') ? 'application/pdf' :
      (resume.filename?.toLowerCase().endsWith('.docx') ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' :
      'text/plain'))

    setSelectedResume({ ...resume, type:detectedType, originalIndex: resume.originalIndex });

    if (!previewCache[resume.originalIndex]) {
      if (resume.file_content && resume.filename) {
        try {
          const byteCharacters = atob(resume.file_content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: resume.type });
          const file = new File([blob], resume.filename, { type: detectedType, lastModified: Date.now() });
          setPreviewCache(prev => ({ ...prev, [resume.originalIndex]: file }));
          setPreviewFile(file);
        } catch (error) {
          const fallbackFile = new File([resume.text_content || 'No content available'], resume.filename, { type: 'text/plain' });
          setPreviewFile(fallbackFile);
        }
      } else {
        setPreviewFile(previewCache[resume.originalIndex]);
      }
    }
    if (!currentCyclingIcon) {
      const randomIcon = cyclingIcons[Math.floor(Math.random() * cyclingIcons.length)];
      setCurrentCyclingIcon(randomIcon);
    }
  };

  const closeModal = () => {
    setSelectedResume(null);
    setCurrentCyclingIcon(null);
    setPreviewFile(null);
    setPreviewCacheStatus(false);
    setShowPreview(false); 
    setIsExpanded(false);
  };

  return (
    <div className='results-container'>
      <h2>Ranked Resumes</h2>
      {rankedResumes.length === 0 ? (
        <p>No resumes to display.</p>
      ) : (
        <div className='results-list'>
          {rankedResumes.map((resume, idx) => (
            <ResultCard
              key={resume.id}
              resume={resume}
              fileIndex={resume.originalIndex}
              jobDescriptionKeywords={jobDescriptionKeywords}
              onClick={() => {handleCardClick(resume); togglePopUp();}}
              onRemoveKeyword={onRemoveKeyword}
              editedFilenameInputs={editedFilenameInputs}
              isWinner={idx === 0}
            />
          ))}
        </div>
      )}

      {selectedResume && (
        <div className='results-modal-overlay' onClick={(e) => { if (e.target === e.currentTarget) { closeModal(); togglePopUp(); } }}>
          <div className={`results-modal-content-overlay ${isExpanded ? 'expanded' : ''}`}>
            <button className='close-button' onClick={() => {closeModal(); togglePopUp();}}><img src={CloseIcon} alt='' /></button>
            <div className={`results-modal-content ${isExpanded ? 'expanded' : ''}`} onClick={(e) => e.stopPropagation()}>
              <h2>{selectedResume.label}</h2>
              <h4> {editedFilenameInputs[selectedResume.originalIndex] ? `${editedFilenameInputs[selectedResume.originalIndex]}` : selectedResume.filename || 'Untitled Resume'}</h4>

              <div className='results-modal-upper'>
                <div className='keyword-score-container'>
                  <div className='keyword-score-upper'>
                    <GeneralTooltip content='Keyword Match Score' position='top' delay={100} verticalOffset={4} triggerClassName='keyword-score-header'>
                      <div className='keyword-score-header'>
                        {(selectedResume.similarity_score * 100).toFixed(1)}%
                      </div>
                    </GeneralTooltip>
                  </div>
                </div>

                <div className='progress-animation-container'>
                  <div className='progress-bar-container results'>
                    <div className='progress-bar results' style={{ width: `${(selectedResume.similarity_score || 0) * 100}%` }} />
                    {currentCyclingIcon && (
                      <div className='lottie-container-results' style={{ left: `${(selectedResume.similarity_score || 0) * 100}%` }}>
                        <Lottie animationData={currentCyclingIcon} loop={true} />
                      </div>
                    )}
                  </div>
                  <div className='finish-flag-container'>
                    <img src={FinishLine} alt='Finish' className='finish-flag-icon' />
                  </div>
                </div>

                <div className='toggle-switch'>
                  <div className={`toggle-container ${showPreview ? 'preview-active' : ''}`}
                       onClick={() => {
                        if (!showPreview && activePreviewIndex !== null) {
                          setPreviewFile(previewCache[activePreviewIndex]);
                          setPreviewCacheStatus(true);
                        }
                        setShowPreview(v => !v);
                       }}>
                    <div className='toggle-option-button list'>
                      <GeneralTooltip content='Overview' position='top' delay={100} verticalOffset={11} triggerClassName='toggle-option-button list'>
                        <SvgIcon src={showPreview ? listOverviewIconOff  : listOverviewIconOn} color='#fff' hoverColor='#6e41c1' size='28px' className='toggle-option-icon' />
                      </GeneralTooltip>
                    </div>
                    <div className='toggle-option-button preview'>
                      <GeneralTooltip content='Preview' position='top' delay={100} verticalOffset={11} triggerClassName='toggle-option-button list'>
                        <SvgIcon src={showPreview ? EyePDFIconOff : EyePDFIconOn} color='#fff' hoverColor='#6e41c1' size='28px' className='toggle-option-icon' />
                      </GeneralTooltip>
                    </div>
                    <div className='toggle-slider'></div>
                  </div>
                </div>
              </div>

              <div className={`preview-modal-content-top embedded ${isExpanded ? 'Expand':''} ${showPreview ? '' : 'hidden'}`}>
                <div className={`embedded-preview-container ${showPreview ? '' : 'hidden'} ${isExpanded ? 'Expand' : ''}`}>
                  {showPreview && (
                    <div className={`embedded-toggle-button-wrapper ${isExpanded ? 'Expand' : ''}`}>
                      <button
                        className='expand-toggle-button'
                        onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        <img src={isExpanded ? collapse_icon : expand_icon} alt={isExpanded ? 'Collapse' : 'Expand'} />
                      </button>
                    </div>
                  )}
                  {previewFile && (
                    <FilePreviewModal 
                      file={previewFile} 
                      onClose={() => setPreviewFile(null)}
                      embedded={true}
                      matchedKeywords={selectedResume.matching_keywords}
                      pageNumber={pageNumber}
                      numPages={numPages}
                      onPageChange={setPageNumber}
                      onDocumentLoad={({ numPages }) => setNumPages(numPages)}
                      onReset={handleResetPreview}
                    />
                  )}
                </div>
              </div>

              {showPreview && numPages > 0 && (
                <div className='pdf-controls-embedded'>
                  <button onClick={() => setPageNumber(p => Math.max(1, p - 1))} disabled={pageNumber <= 1}>← Previous</button>
                  <span>Page {pageNumber} of {numPages}</span>
                  <button onClick={() => setPageNumber(p => Math.min(numPages, p + 1))} disabled={pageNumber >= numPages}>Next →</button>
                </div>
              )}

              {!showPreview && (
                <div className='results-modal-lower-content'>
                  <div className='results-keyword-section'>
                    <div className='results-keyword-section-header'>
                      <h3 className='results-keyword-section-title'>Matched Keywords</h3>
                      <GeneralTooltip content={<div style={{ padding: '4px' }}>{copyStatus.copied ? 'Copied!' : 'Copy keywords'}</div>} position='right' delay={100} verticalOffset={4} triggerClassName='copy-keywords-button results'>
                        <button onClick={(e) => { e.stopPropagation(); copyToClipboard(jobDescriptionKeywords.filter(kw => selectedResume.matching_keywords.includes(kw)).sort((a, b) => a.localeCompare(b)).join(', '), 'matched'); }} className='copy-keywords-button results'>
                          <img src={copiedSection === 'matched' ? CheckIcon : CopyIcon} alt={copiedSection === 'matched' ? 'Copied!' : 'Copy keywords'} />
                        </button>
                      </GeneralTooltip>
                    </div>
                    <div className='results-keywords-container'>
                      {jobDescriptionKeywords.filter(kw => selectedResume.matching_keywords.includes(kw)).sort((a, b) => a.localeCompare(b)).map((keyword, displayIndex, array) => (
                        <span key={keyword} className='results-keyword-item'>
                          {keyword}{displayIndex !== array.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className='results-keyword-section'>
                    <div className='results-keyword-section-header'>
                      <h3 className='results-keyword-section-title'>Missing Keywords</h3>
                      <GeneralTooltip content={<div style={{ padding: '4px' }}>{copyStatus.copied ? 'Copied!' : 'Copy keywords'}</div>} position='top' delay={100} verticalOffset={4} triggerClassName='copy-keywords-button results'>
                        <button onClick={(e) => { e.stopPropagation(); copyToClipboard(jobDescriptionKeywords.filter((kw) => !selectedResume.matching_keywords.includes(kw)).sort((a, b) => a.localeCompare(b)).join(', '), 'missing'); }} className='copy-keywords-button results'>
                          <img src={copiedSection === 'missing' ? CheckIcon : CopyIcon} alt={copiedSection === 'missing' ? 'Copied!' : 'Copy keywords'} />
                        </button>
                      </GeneralTooltip>
                    </div>
                    <div className='results-keywords-container'>
                      {jobDescriptionKeywords.filter((kw) => !selectedResume.matching_keywords.includes(kw)).sort((a, b) => a.localeCompare(b)).join(', ')}
                    </div>
                  </div>
                  <ExtractSnippet content={selectedResume.content} collapsedHeight='200px' expandedMinHeight='300px' />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ResultsDisplay.propTypes = {
  rankedResumes: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    similarity_score: PropTypes.number,
    matching_keywords: PropTypes.array,
    preview_image: PropTypes.string,
    content: PropTypes.string,
    editedFilenameInputs: PropTypes.object,
    fileIndex: PropTypes.number,
    filename: PropTypes.string,
    file_content: PropTypes.string,
    type: PropTypes.string,  
    originalIndex: PropTypes.number
  })),
  jobDescriptionKeywords: PropTypes.array,
  onClick: PropTypes.func,
  onRemoveKeyword: PropTypes.func
};

ResultsDisplay.defaultProps = {
  resume: {
    label: 'Untitled Resume',
    similarity_score: 0,
    matching_keywords: [],
    jobDescriptionKeywords: [],
    content: '',
    editedFilenameInputs: {},
    fileIndex: null,
    filename: '',
    file_content: '',
    type: 'application/pdf',
    originalIndex: null
  },
  jobDescriptionKeywords: [],
  onClick: () => {},
  onRemoveKeyword: () => {}
};

export default ResultsDisplay;


